import {TabView} from "primereact/tabview";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {matchPath, Navigate, Outlet, useLocation} from "react-router-dom";

import {useAppreciationsVisible} from "../component/appreciation/appreciationHooks";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import {getTabPanelWithPermission} from "../component/TabPanelWithPermission";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {useCheckPermissionRequirement} from "../hooks/permissionHooks";
import {selectPermissions} from "../redux/authSlice";
import {selectPurpose, selectPurposePolicy, selectRevaId} from "../redux/persistenceSlice";
import {selectHasValueOrders} from "../redux/valueOrdersSlice";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import {PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS} from "../util/permissions";
import {valuationEntryRoute} from "../util/purposeResolution";

const ALL_TABS = (hasRevaId, hasValueOrders, appreciationsVisible) => [
    {
        route: "documents",
        headerI18n: "audit.tab_documents.header",
        disabled: !hasRevaId || !hasValueOrders,
    },
    // Der Würdigungs-Tab hängt komplett an der Berechtigung (#18607) — ohne sie
    // wird er nicht (auch nicht gesperrt) angezeigt.
    ...(appreciationsVisible
        ? [
              {
                  route: "appreciations",
                  headerI18n: "audit.tab_appreciations.header",
                  disabled: !hasRevaId,
              },
          ]
        : []),
    {
        route: "changelog",
        headerI18n: "audit.tab_changelog.header",
        disabled: !hasRevaId,
    },
];

/**
 * @returns {React.JSX.Element}
 */
function PageAudit() {
    const {t} = useTranslation();
    const navigate = useNavigateWithQuery();
    const revaId = useSelector(selectRevaId);
    const hasValueOrders = useSelector(selectHasValueOrders);
    const permissions = useSelector(selectPermissions);
    const appreciationsVisible = useAppreciationsVisible();
    const {pathname, search} = useLocation();
    // #19481: Zwecke ohne Prüfbereich (Research) — die Sidebar blendet „Prüfung" aus, ein direkt aufgerufenes
    // /audit/* führt auf die Einstiegsseite des Zwecks.
    const {auditAllowed} = useSelector(selectPurposePolicy);
    const purpose = useSelector(selectPurpose);
    const objectPageAllowed = useCheckPermissionRequirement(PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS);
    const hasRevaId = revaId !== null;
    useEffectRequiresValidRevaId();
    const availableTabRoutes = ALL_TABS(hasRevaId, hasValueOrders, appreciationsVisible).map((tab) => tab.route);
    let activeIndex = null;
    for (let index = 0; index < availableTabRoutes.length; index++) {
        if (matchPath(`/audit/${availableTabRoutes[index]}`, pathname)) {
            // Korrektur: [index] hinzugefügt
            activeIndex = index;
            break;
        }
    }

    const onTabChange = (index) => {
        if (index >= 0 && index < availableTabRoutes.length) {
            navigate(`/audit/${availableTabRoutes[index]}`);
        }
    };

    const tabs = ALL_TABS(hasRevaId, hasValueOrders, appreciationsVisible);

    if (!auditAllowed) {
        return (
            <Navigate to={{pathname: valuationEntryRoute(purpose, permissions, objectPageAllowed), search}} replace />
        );
    }

    return (
        <MainFrame>
            <MainToolbar title={t("navigation.audit")}>
                <TabView
                    className="flex-1 HeaderTabView"
                    panelContainerClassName="hidden"
                    activeIndex={activeIndex}
                    onTabChange={(e) => onTabChange(e.index)}
                >
                    {tabs.map((tab) => getTabPanelWithPermission(tab, t, permissions))}
                </TabView>
            </MainToolbar>
            <Outlet />
        </MainFrame>
    );
}

export default PageAudit;
